import { watch, onUnmounted } from 'vue';
import { useSecurityStore } from '../stores/security';
import { playSoundLoop, stopSound } from '../utils/alarmSounds';

/**
 * Global security alarm siren.
 *
 * While the security system has an active alarm, play the siren for
 * 10 seconds and repeat every minute. A re-trigger (alarm goes off and
 * comes back) restarts the cycle immediately.
 */
const SIREN_DURATION_MS = 10_000;
const REPEAT_INTERVAL_MS = 60_000;

export function useAlarmSiren(): void {
  const securityStore = useSecurityStore();

  let cycleInterval: ReturnType<typeof setInterval> | null = null;
  let sirenStopTimer: ReturnType<typeof setTimeout> | null = null;

  function clearTimers() {
    if (cycleInterval) {
      clearInterval(cycleInterval);
      cycleInterval = null;
    }
    if (sirenStopTimer) {
      clearTimeout(sirenStopTimer);
      sirenStopTimer = null;
    }
  }

  function startSirenCycle() {
    console.debug('[AlarmSiren] Starting 10s siren cycle');
    void playSoundLoop('classic-alarm');
    sirenStopTimer = setTimeout(() => {
      console.debug('[AlarmSiren] Siren cycle ended');
      stopSound();
      sirenStopTimer = null;
    }, SIREN_DURATION_MS);
  }

  watch(
    () => securityStore.alarmActive,
    (active) => {
      clearTimers();
      stopSound();
      if (!active) {
        console.debug('[AlarmSiren] Stopped (alarm cleared)');
        return;
      }

      // Immediate siren on trigger, then repeat every minute while active.
      startSirenCycle();
      cycleInterval = setInterval(() => {
        startSirenCycle();
      }, REPEAT_INTERVAL_MS);
    },
    { immediate: true },
  );

  onUnmounted(() => {
    clearTimers();
    stopSound();
  });
}
